/**
 * Sponsor Service - API calls for sponsor functionality
 */

import { getIdToken } from './cognitoService';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') ?? '';

export interface AvailableShaab {
  id: string;
  name: string;
  sahabeName?: string;
  image: string;
  bio?: string;
  isSponsored: boolean;
  canClaim: boolean;
}

export interface Sponsorship {
  avatarId: string;
  avatarName: string;
  sahabeName?: string;
  sponsor: {
    displayName: string;
    notes: string | null;
    fundedAt: string;
  };
}

export interface AvailableShaabsResponse {
  available: AvailableShaab[];
  sponsored: AvailableShaab[];
  totalAvailable: number;
  totalSponsored: number;
}

export interface AllSponsorsResponse {
  sponsorships: Sponsorship[];
  total: number;
}

export interface ClaimSponsorRequest {
  amount: number;
  currency?: string;
  isPublic?: boolean;
  notes?: string;
  displayName?: string;
}

export interface ClaimSponsorResponse {
  success: boolean;
  message: string;
  sponsorship: {
    avatarId: string;
    avatarName: string;
    sponsorType: string;
    amount: number;
    currency: string;
    fundedAt: string;
  };
}

/**
 * Get available (unfunded) and sponsored shaabs - public, no login required
 */
export async function getAvailableShaabsPublic(): Promise<AvailableShaabsResponse> {
  const response = await fetch(`${API_URL}/sponsor/available`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to get available shaabs');
  }

  return response.json();
}

/**
 * Get all sponsors with notes (for banner display) - public, no login required
 */
export async function getAllSponsorshipsPublic(): Promise<AllSponsorsResponse> {
  const response = await fetch(`${API_URL}/sponsor/all`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to get sponsorships');
  }

  return response.json();
}

/**
 * Get available (unfunded) shaabs for sponsorship (authenticated)
 */
export async function getAvailableShaabs(): Promise<AvailableShaabsResponse> {
  const token = await getIdToken();

  const response = await fetch(`${API_URL}/sponsor/available`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get available shaabs');
  }

  return response.json();
}

/**
 * Claim exclusive sponsorship for an avatar
 */
export async function claimSponsorship(
  avatarId: string,
  request: ClaimSponsorRequest,
): Promise<ClaimSponsorResponse> {
  const token = await getIdToken();

  const response = await fetch(`${API_URL}/avatar/${avatarId}/sponsor/donation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (response.status === 409) {
    const error = await response.json();
    throw new Error(error.message || 'This companion has already been sponsored');
  }

  if (!response.ok) {
    throw new Error('Failed to claim sponsorship');
  }

  return response.json();
}

/**
 * Get sponsor info for a specific avatar
 */
export async function getSponsorInfo(avatarId: string): Promise<{
  avatarId: string;
  avatarName: string;
  sponsorType: string;
  isSponsored: boolean;
  canClaim: boolean;
  sponsor: {
    userName: string;
    amount: number;
    currency: string;
    fundedAt: string;
    notes?: string;
  } | null;
}> {
  const token = await getIdToken();

  const response = await fetch(`${API_URL}/avatar/${avatarId}/sponsor/donation`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get sponsor info');
  }

  return response.json();
}
